import type { MaybeRefOrGetter } from "vue";
import { toValue } from "vue";
import type {
  BlogPost,
  BlogPostListItem,
  BlogListResponse,
  BlogCategory,
} from "~/slices/blog/types";

const BLOG_ITEMS_PER_PAGE = 12;

type BlogPostResult = { data: Ref<BlogPost | null> };
type BlogListResult = {
  data: Ref<BlogPostListItem[]>;
  count: Ref<number>;
  pending: Ref<boolean>;
  refresh: () => Promise<void>;
};
type BlogCategoriesResult = { data: Ref<BlogCategory[]> };

export const useAsyncBlogPost = (
  slug: MaybeRefOrGetter<string>,
): BlogPostResult => {
  const config = useRuntimeConfig();
  const { authFetch } = useAuthFetch();
  const { data } = useAsyncData(
    () => `blog-${toValue(slug)}`,
    async () => {
      try {
        return await authFetch<BlogPost>(
          `${config.public.baseURL}v1/blogs/${toValue(slug)}/`,
        );
      } catch (e: unknown) {
        const err = e as { statusCode?: number };
        if (err?.statusCode === 404) return null;
        throw e;
      }
    },
    { watch: [slug] },
  );

  return { data: data as Ref<BlogPost | null> };
};

export const useBlogCategories = (): BlogCategoriesResult => {
  const config = useRuntimeConfig();
  const { authFetch } = useAuthFetch();

  const { data } = useAsyncData("blog-categories", () =>
    authFetch<BlogCategory[]>(`${config.public.baseURL}v1/blogs-categories/`),
  );

  return { data: data as Ref<BlogCategory[]> };
};

export const useBlogList = (
  page: Ref<number>,
  search: Ref<string>,
  categories: Ref<string>,
): BlogListResult => {
  const config = useRuntimeConfig();
  const route = useRoute();
  const { authFetch } = useAuthFetch();

  const key = computed(
    () =>
      `blog-list-${route.path}-${page.value}-${search.value}-${categories.value}`,
  );

  const {
    data: listData,
    pending,
    refresh,
  } = useAsyncData(
    key,
    () => {
      const params: Record<string, string | number> = {
        limit: BLOG_ITEMS_PER_PAGE,
        offset: (page.value - 1) * BLOG_ITEMS_PER_PAGE,
      };
      if (search.value) params.search = search.value;
      if (categories.value) params.categories = categories.value;

      return authFetch<BlogListResponse>(
        `${config.public.baseURL}v1/blogs/`,
        { params },
      );
    },
    { watch: [page, search, categories] },
  );

  const data = computed(() => listData.value?.results ?? []);
  const count = computed(() => listData.value?.count ?? 0);

  return {
    data: data as Ref<BlogPostListItem[]>,
    count: count as Ref<number>,
    pending,
    refresh,
  };
};

export { BLOG_ITEMS_PER_PAGE };
